import { CHANNEL_KIND } from '../catalog/channel';
import { CONTROL_KIND } from '../catalog/control';
import type { ControlKind } from '../catalog/control';
import { ISSUE, ISSUE_TARGET, configIssue } from '../catalog/issue';
import { LOAD_TYPE_SPECS } from '../catalog/loadType';
import {
  DIN_CHANNEL_MAX,
  DIN_CHANNEL_NUMBERING,
  DIN_VALUE,
  RELAY_VALUE,
  firmwareChannel,
  toDimmerValue,
  travelValue,
} from '../catalog/dinValue';
import { DIN_FIRMWARE_TYPE, RCU_CMDTYPE, RCU_COMMAND, RCU_GROUP, RCU_TOP_TYPE } from '../catalog/protocol';
import type { RcuCmdType } from '../catalog/protocol';
import type { RailId } from '../ids';
import type { RcuConfig } from '../model';
import { findRail } from '../query/lookup';
import type { Result } from '../result';
import { fail, ok } from '../result';
import { assertNever } from '../../exhaustive';
import type { RcuOperateRequest } from '../wire';
import type { RcuCommand } from './command';
import { describeCommand } from './command';

/**
 * Turns a guest action into the firmware's own command document.
 *
 * Everything is resolved against the configuration first: a load's channel
 * decides whether the command is an onboard `rcuoperate` or a DIN `dinoperate`,
 * and a rail supplies the module type and address. A command the firmware has no
 * equivalent for is refused rather than approximated.
 */

/** A relay is either off or on, whichever module drives it. */
export const relayValue = (on: boolean): number => (on ? RELAY_VALUE.ON : RELAY_VALUE.OFF);

/** Motors take a direction rather than a position; stopping is the zero value. */
export { travelValue };

export const payloadCmdType = (payload: RcuOperateRequest): RcuCmdType => payload.data.cmdtype;

const unsupported = (command: RcuCommand, detail: string): Result<RcuOperateRequest> =>
  fail(
    configIssue(
      ISSUE.COMMAND_UNSUPPORTED,
      { kind: ISSUE_TARGET.RCU },
      `${detail} on ${command.load.name}`,
    ),
  );

/** The one onboard command: relays 1–26, on or off. */
const rcuOperate = (command: RcuCommand, channel: number): Result<RcuOperateRequest> => {
  if (command.kind !== RCU_COMMAND.SET_POWER) {
    return unsupported(command, describeCommand(command));
  }
  return ok({
    type: RCU_TOP_TYPE.COMMAND,
    data: {
      cmdtype: RCU_CMDTYPE.OPERATE_RELAY,
      groupid: RCU_GROUP.DEFAULT,
      relayno: channel,
      value: relayValue(command.on),
    },
  });
};

/**
 * What a DIN channel should be driven to for this command, by control kind:
 *
 * - toggle: off or on.
 * - level: the module's 0–100 scale, where zero is off.
 * - position: a direction — open, close or stop — because the modules this app
 *   drives have no absolute position to send.
 */
const dinValue = (command: RcuCommand, control: ControlKind): number | null => {
  switch (control) {
    case CONTROL_KIND.TOGGLE:
      return command.kind === RCU_COMMAND.SET_POWER ? relayValue(command.on) : null;

    case CONTROL_KIND.LEVEL:
      if (command.kind !== RCU_COMMAND.SET_LEVEL) return null;
      return command.on ? toDimmerValue(command.level) : DIN_VALUE.MIN;

    case CONTROL_KIND.POSITION:
      if (command.kind === RCU_COMMAND.SET_TRAVEL) return travelValue(command.travel);
      // The header switch on a curtain is still an open/close.
      if (command.kind === RCU_COMMAND.SET_POWER) {
        return command.on ? DIN_VALUE.INCREASE : DIN_VALUE.DECREASE;
      }
      return null;

    default:
      return assertNever(control);
  }
};

const dinOperate = (
  config: RcuConfig,
  command: RcuCommand,
  channel: number,
  railId: RailId,
): Result<RcuOperateRequest> => {
  const rail = findRail(config, railId);
  if (rail === undefined) {
    return fail(
      configIssue(ISSUE.UNKNOWN_RAIL, { kind: ISSUE_TARGET.RAIL, id: railId }, command.load.name),
    );
  }

  if (channel < DIN_CHANNEL_NUMBERING.CONSOLE_FIRST) {
    return fail(
      configIssue(ISSUE.CHANNEL_OUT_OF_RANGE, { kind: ISSUE_TARGET.RAIL, id: rail.id }, String(channel)),
    );
  }
  const wireChannel = firmwareChannel(channel);
  if (wireChannel > DIN_CHANNEL_MAX) {
    return fail(
      configIssue(ISSUE.CHANNEL_OUT_OF_RANGE, { kind: ISSUE_TARGET.RAIL, id: rail.id }, String(channel)),
    );
  }

  const value = dinValue(command, LOAD_TYPE_SPECS[command.load.type].control);
  if (value === null) {
    return unsupported(command, describeCommand(command));
  }

  return ok({
    type: RCU_TOP_TYPE.COMMAND,
    data: {
      cmdtype: RCU_CMDTYPE.OPERATE_DIN,
      groupid: RCU_GROUP.DEFAULT,
      dintype: DIN_FIRMWARE_TYPE[rail.type],
      addr: rail.address,
      channel: wireChannel,
      value,
    },
  });
};

/** Resolves a command against the configuration into the wire document. */
export const buildPayload = (config: RcuConfig, command: RcuCommand): Result<RcuOperateRequest> => {
  const { channel } = command;
  return channel.kind === CHANNEL_KIND.RCU
    ? rcuOperate(command, channel.channel)
    : dinOperate(config, command, channel.channel, channel.railId);
};
